import React, { useState, useRef } from "react";
import { App } from "antd";
import {
  UploadOutlined, FilePdfOutlined, ScissorOutlined,
  DownloadOutlined, DeleteOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { downloadAuthenticatedFile } from "../../utils/downloadFile";
import "../PdfTools.css";
import "./SplitPdf.css";

const countPdfPages = async (pdfFile) => {
  const buffer = await pdfFile.arrayBuffer();
  const pdfText = new TextDecoder("latin1").decode(buffer);
  const pageMatches = pdfText.match(/\/Type\s*\/Page\b(?!s)/g);

  if (pageMatches?.length) {
    return pageMatches.length;
  }

  const countMatches = [...pdfText.matchAll(/\/Count\s+(\d+)/g)]
    .map((match) => Number(match[1]))
    .filter((count) => Number.isFinite(count) && count > 0);

  return countMatches.length ? Math.max(...countMatches) : 0;
};

const SplitPdf = () => {
  const { message } = App.useApp();
  const [file,           setFile]          = useState(null);
  const [uploading,      setUploading]     = useState(false);
  const [downloadingFile, setDownloadingFile] = useState("");
  const [readingPages,   setReadingPages]  = useState(false);
  const [pageReadFailed, setPageReadFailed] = useState(false);
  const [dragOver,       setDragOver]      = useState(false);
  const [pageCount,      setPageCount]     = useState(0);
  const [selectedPages,  setSelectedPages] = useState(new Set());
  const [results,        setResults]       = useState(null); // { selected, remaining }
  const fileRef = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  const pickFile = async (incoming) => {
    const f = Array.from(incoming).find((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (!f) { message.error("Please select a PDF file."); return; }
    setFile(f);
    setSelectedPages(new Set());
    setResults(null);
    setDownloadingFile("");
    setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(true);

    try {
      const detectedPageCount = await countPdfPages(f);
      if (detectedPageCount > 0) {
        setPageCount(detectedPageCount);
      } else {
        setPageReadFailed(true);
        message.warning("Unable to read page count automatically. Please enter it manually.");
      }
    } catch {
      setPageReadFailed(true);
      message.warning("Unable to read page count automatically. Please enter it manually.");
    } finally {
      setReadingPages(false);
    }
  };

  const reset = () => {
    setFile(null);
    setSelectedPages(new Set());
    setResults(null);
    setDownloadingFile("");
    setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(false);
  };

  const downloadSplitFile = async (data, key) => {
    if (!data?.downloadUrl || downloadingFile) return;
    setDownloadingFile(key);

    try {
      await downloadAuthenticatedFile(data.downloadUrl, {
        token: localStorage.getItem("token") ?? "",
        fileName: data.fileName || "split.pdf",
      });
    } catch {
      message.error("Unable to download PDF. Please try again.");
    } finally {
      setDownloadingFile("");
    }
  };

  const togglePage = (n) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });
  };

  const handleSplit = async () => {
    if (!file) { message.warning("Please select a PDF file."); return; }
    if (selectedPages.size === 0) { message.warning("Select at least one page to split."); return; }
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("Source", isModule ? "Private-Web" : "Public-Web");
    [...selectedPages].sort((a, b) => a - b).forEach((p) =>
      formData.append("selectedPages[]", p.toString())
    );

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/split-pdf-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!res.data.error && res.data.data) {
        setResults({
          selected:  res.data.data.selectedPagesFile,
          remaining: res.data.data.remainingPagesFile,
        });
        message.success("PDF split successfully!");
      } else {
        message.error(res.data.message || "Split failed.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const pages = pageCount > 0
    ? Array.from({ length: pageCount }, (_, i) => i + 1)
    : [];

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`pt-root ${isModule ? "pt-module" : ""}`}>
        <div className="pt-inner">

          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: "#eff6ff" }}>
              <ScissorOutlined style={{ color: "#0891b2" }} />
            </div>
            <div>
              <h1 className="pt-title">Split PDF</h1>
              <p className="pt-subtitle">Select the pages you want to extract into a separate PDF file.</p>
            </div>
          </div>

          {/* Drop zone */}
          {!file && !results && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
                onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button className="pt-choose-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {/* File + page selector */}
          {file && !results && (
            <>
              <div className="pt-file-row">
                <FilePdfOutlined className="pt-file-icon" />
                <span className="pt-file-name">{file.name}</span>
                <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                <button className="pt-file-remove" onClick={reset}><DeleteOutlined /></button>
              </div>

              <div className="sp-page-count-wrap">
                <label className="sp-page-label">
                  {readingPages
                    ? "Reading PDF page count..."
                    : pageReadFailed
                      ? "How many pages does this PDF have?"
                      : "PDF pages detected"}
                </label>
                {readingPages ? (
                  <span className="sp-page-reading">
                    <span className="pt-spinner" /> Checking
                  </span>
                ) : pageReadFailed ? (
                  <input
                    type="number"
                    min="1"
                    max="500"
                    className="sp-page-input"
                    placeholder="e.g. 10"
                    value={pageCount || ""}
                    onChange={(e) => { setPageCount(parseInt(e.target.value) || 0); setSelectedPages(new Set()); }}
                  />
                ) : (
                  <span className="sp-page-detected">{pageCount} page{pageCount !== 1 ? "s" : ""}</span>
                )}
              </div>

              {/* Page grid */}
              {pages.length > 0 && (
                <>
                  <div className="sp-grid-hint">
                    Tap pages to select them for extraction
                    {selectedPages.size > 0 && (
                      <span className="sp-selected-count">{selectedPages.size} selected</span>
                    )}
                  </div>
                  <div className="sp-page-grid">
                    {pages.map((n) => (
                      <button
                        key={n}
                        className={`sp-page-tile ${selectedPages.has(n) ? "selected" : ""}`}
                        onClick={() => togglePage(n)}
                      >
                        <FilePdfOutlined className="sp-tile-icon" />
                        <span className="sp-tile-label">Page {n}</span>
                        {selectedPages.has(n) && (
                          <span className="sp-tile-check">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <button
                className="pt-action-btn"
                onClick={handleSplit}
                disabled={!file || selectedPages.size === 0 || uploading}
              >
                {uploading ? <span className="pt-spinner" /> : <ScissorOutlined />}
                {uploading ? "Splitting…" : `Split PDF (${selectedPages.size} page${selectedPages.size !== 1 ? "s" : ""})`}
              </button>
            </>
          )}

          {/* Results */}
          {results && (
            <div className="sp-results">
              <div className="pt-success-icon" style={{ fontSize: 48, color: "#10b981", textAlign: "center" }}>
                <CheckCircleFilled />
              </div>
              <div className="pt-success-title" style={{ textAlign: "center", fontFamily: "Poppins", fontSize: 20, fontWeight: 700, color: "#065f46" }}>
                Split Complete!
              </div>
              <div className="sp-result-cards">
                {[
                  { key: "selected", label: "Selected Pages", data: results.selected, color: "#0891b2", bg: "#eff6ff" },
                  { key: "remaining", label: "Remaining Pages", data: results.remaining, color: "#1a7a43", bg: "#f0fdf4" },
                ].filter(({ data }) => Boolean(data)).map(({ key, label, data, color, bg }) => (
                  <div key={label} className="sp-result-card">
                    <div className="sp-result-card-icon" style={{ background: bg }}>
                      <FilePdfOutlined style={{ color, fontSize: 24 }} />
                    </div>
                    <div className="sp-result-card-body">
                      <div className="sp-result-label">{label}</div>
                      <div className="sp-result-name">{data?.fileName || "file.pdf"}</div>
                    </div>
                    <button
                      type="button"
                      className="sp-result-download"
                      style={{ borderColor: color, color }}
                      onClick={() => downloadSplitFile(data, key)}
                      disabled={Boolean(downloadingFile)}
                    >
                      {downloadingFile === key ? <span className="sp-result-spinner" /> : <DownloadOutlined />}
                      {downloadingFile === key ? "Downloading..." : "Download"}
                    </button>
                  </div>
                ))}
              </div>
              <button className="pt-restart-btn" style={{ margin: "0 auto" }} onClick={reset}>
                Split another PDF
              </button>
            </div>
          )}
        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default SplitPdf;
